// `useCompleteWithCleanup` wraps an `onComplete` chain that can be nested:
// e.g. `buildUnifiedAccount`'s own handleComplete (which awaits a consumer
// hook and cleans up on failure, respecting `apiHandlers`) sits *inside*
// `UnifiedAccountModal`'s `onComplete` — itself wrapped in
// `useCompleteWithCleanup`. Without this marker, a single failure would
// trigger cleanup (and an error report) twice: once from the inner layer,
// once from the outer one.
//
// The failing promise still needs to propagate all the way up to the auth
// flow machine so it can route the attempt back to a retryable state, so we
// can't just swallow the error where it's first handled — we mark it instead,
// so the next layer up knows cleanup already happened and skips its own.
// Symbol.for (not Symbol()) so the marker still matches if this module ends
// up duplicated across bundles/chunks (e.g. a page loading two versions of
// unified-auth-core-lib) — Symbol.for resolves through the global symbol
// registry, so every copy gets the same symbol for the same key, unlike a
// plain Symbol() which would be a distinct value per module instance.
const CLEANUP_HANDLED = Symbol.for('unified-auth-core-lib/cleanupHandled');

// Fallback for frozen/sealed/non-extensible errors where defineProperty
// silently fails. Per-module-instance only (two bundled copies get separate
// WeakSets), but the primary Symbol.for path still covers cross-bundle;
// this just closes the gap for immutable error objects.
const fallbackSet = new WeakSet<object>();

export function markCleanupHandled(error: unknown): void {
  if (error && typeof error === 'object') {
    try {
      Object.defineProperty(error, CLEANUP_HANDLED, {
        value: true,
        enumerable: false,
      });
    } catch {
      fallbackSet.add(error);
    }
  }
}

export function isCleanupHandled(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return (
    Boolean((error as Record<symbol, unknown>)[CLEANUP_HANDLED]) ||
    fallbackSet.has(error)
  );
}
